// 2D nesting / cut-list optimisation.
//
// Algorithm: First-Fit-Decreasing-Height (FFDH) with shelves.
//   Industry-standard simple bin-packer. Not optimal — beats raw greedy
//   by ~15% in waste vs perfect packing. Phase-3.5 swap-in for cutlist-
//   optimizer-cli is gated by `Project.nestCutList()` so the UI doesn't
//   change when we upgrade the back-end.
//
// Grain handling:
//   • material.grainDir === "none"   → rotation allowed freely
//   • material.grainDir === "length" → part's grain must align with the
//     sheet's long axis (we treat sheet length as max(W,H), which is
//     standard for 49×97 sheets where 97 is the grain direction)
//   • part.grain === "none"          → rotation allowed regardless
//   • part.grain === "length"/"width" → respected: rotate iff that
//     orients the part with the sheet grain

import type {
  Cabinet,
  CabinetPart,
  Id,
  Material,
} from "@/lib/types";
import { type CatalogIndex, partsFor } from "./bom";

// ── Constants ────────────────────────────────────────────────────

/** 1/8" saw kerf. Adds to spacing between adjacent parts. */
export const KERF = 0.125;

// ── Output shape ─────────────────────────────────────────────────

export interface NestPlacement {
  /** Position on the sheet in inches. (0,0) = bottom-left. */
  x: number;
  y: number;
  /** Final dimensions AFTER rotation. */
  w: number;
  h: number;
  rotated: boolean;

  /** Source. */
  partId: string;
  partName: string;
  cabinetId: Id;
  cabinetClassId: string;
}

export interface NestSheet {
  sheetIndex: number;
  placements: NestPlacement[];
  /** Sum of placed part areas (sq inches). */
  usedArea: number;
}

export interface NestBin {
  materialId: Id;
  materialName: string;
  sheetW: number;
  sheetH: number;
  grainDir: Material["grainDir"];
  sheets: NestSheet[];
  /** total sheets = sheets.length. */
  totalSheets: number;
  /** sheetW × sheetH × totalSheets in sq inches. */
  totalArea: number;
  /** sum of placements' areas in sq inches. */
  usedArea: number;
  /** (totalArea - usedArea) / totalArea, 0..1. */
  wastePercent: number;
}

export interface ProjectNesting {
  bins: NestBin[];
  unplaceable: Array<{ partName: string; reason: string }>;
  overallWastePercent: number;
}

// ── Internal types ───────────────────────────────────────────────

interface Shelf {
  y: number;
  height: number;
  occupiedW: number;
  placements: NestPlacement[];
}

interface SheetState {
  shelves: Shelf[];
  placements: NestPlacement[];
}

interface PartInput {
  partId: string;
  partName: string;
  cabinetId: Id;
  cabinetClassId: string;
  w: number;
  h: number;
  grain: CabinetPart["grain"];
}

// ── FFDH driver ──────────────────────────────────────────────────

function pickOrientation(p: PartInput, material: Material): { w: number; h: number; rotated: boolean } {
  const gd = material.grainDir;
  // If sheet is grain-agnostic OR part is grain-agnostic, rotate freely
  // (we'll pick the orientation that's wider — improves shelf packing
  // by reducing shelf-height waste).
  if (gd === "none" || p.grain === "none") {
    // Lay parts long-side horizontally (helps shelf algorithm).
    if (p.w < p.h) return { w: p.h, h: p.w, rotated: true };
    return { w: p.w, h: p.h, rotated: false };
  }
  // Both sheet and part have a grain direction.
  //   - We treat sheet grain as running along the long axis (max(W,H)).
  //   - Part with grain="length" wants its longer dim aligned with sheet
  //     grain. Since we keep sheet in its given W×H, grain runs along W
  //     if W >= H, else along H. We rotate to align.
  const sheetGrainAlongW = material.sheetW >= material.sheetH;
  const wantWLong = (p.grain === "length") === sheetGrainAlongW;
  if (wantWLong) {
    // We want the part's "length" axis on W. The part's length is whichever
    // is larger of p.w / p.h after our convention (parts in cabinets.ts have
    // grain along "length" = first dimension, by convention p.w).
    return { w: p.w, h: p.h, rotated: false };
  }
  return { w: p.h, h: p.w, rotated: true };
}

function fitOnSheet(
  state: SheetState,
  oriented: { w: number; h: number; rotated: boolean },
  src: PartInput,
  sheetW: number,
  sheetH: number,
): boolean {
  const { w, h, rotated } = oriented;
  // Try existing shelves (first-fit).
  for (const shelf of state.shelves) {
    if (h <= shelf.height && shelf.occupiedW + KERF + w <= sheetW) {
      const placement: NestPlacement = {
        x: shelf.occupiedW > 0 ? shelf.occupiedW + KERF : 0,
        y: shelf.y,
        w, h, rotated,
        partId: src.partId,
        partName: src.partName,
        cabinetId: src.cabinetId,
        cabinetClassId: src.cabinetClassId,
      };
      shelf.placements.push(placement);
      state.placements.push(placement);
      shelf.occupiedW = placement.x + w;
      return true;
    }
  }
  // Start new shelf.
  const topY = state.shelves.length
    ? state.shelves[state.shelves.length - 1]!.y + state.shelves[state.shelves.length - 1]!.height + KERF
    : 0;
  if (topY + h > sheetH) return false;
  if (w > sheetW) return false;
  const placement: NestPlacement = {
    x: 0, y: topY, w, h, rotated,
    partId: src.partId,
    partName: src.partName,
    cabinetId: src.cabinetId,
    cabinetClassId: src.cabinetClassId,
  };
  const shelf: Shelf = { y: topY, height: h, occupiedW: w, placements: [placement] };
  state.shelves.push(shelf);
  state.placements.push(placement);
  return true;
}

function newSheet(): SheetState {
  return { shelves: [], placements: [] };
}

function nestMaterial(
  parts: PartInput[],
  material: Material,
  unplaceable: ProjectNesting["unplaceable"],
): NestBin {
  // FFDH: sort by height descending. Use max(w,h) so very wide thin parts
  // (toe kicks, stretchers) still get a sensible shelf.
  const sorted = [...parts]
    .map((p) => ({ p, oriented: pickOrientation(p, material) }))
    .sort((a, b) => b.oriented.h - a.oriented.h);

  const sheets: SheetState[] = [];
  let current = newSheet();
  sheets.push(current);

  for (const { p, oriented } of sorted) {
    if (oriented.w > material.sheetW || oriented.h > material.sheetH) {
      unplaceable.push({
        partName: p.partName,
        reason: `${oriented.w.toFixed(1)}×${oriented.h.toFixed(1)} exceeds sheet ${material.sheetW}×${material.sheetH}`,
      });
      continue;
    }
    if (!fitOnSheet(current, oriented, p, material.sheetW, material.sheetH)) {
      current = newSheet();
      sheets.push(current);
      if (!fitOnSheet(current, oriented, p, material.sheetW, material.sheetH)) {
        // Pathological — shouldn't happen since we passed the over-size check above
        unplaceable.push({ partName: p.partName, reason: "fit failed unexpectedly" });
      }
    }
  }

  // Drop the last sheet if it's empty (degenerate case: 0 parts).
  while (sheets.length > 0 && sheets[sheets.length - 1]!.placements.length === 0) {
    sheets.pop();
  }

  const out: NestSheet[] = sheets.map((s, i) => ({
    sheetIndex: i,
    placements: s.placements,
    usedArea: s.placements.reduce((acc, pl) => acc + pl.w * pl.h, 0),
  }));

  const totalArea = out.length * material.sheetW * material.sheetH;
  const usedArea  = out.reduce((acc, s) => acc + s.usedArea, 0);

  return {
    materialId: material._id,
    materialName: material.name,
    sheetW: material.sheetW,
    sheetH: material.sheetH,
    grainDir: material.grainDir,
    sheets: out,
    totalSheets: out.length,
    totalArea,
    usedArea,
    wastePercent: totalArea > 0 ? (totalArea - usedArea) / totalArea : 0,
  };
}

// ── Public entry ─────────────────────────────────────────────────

export function nestProject(
  cabinets: Cabinet[],
  cat: CatalogIndex,
): ProjectNesting {
  const byMaterial = new Map<Id, PartInput[]>();
  for (const cab of cabinets) {
    const parts = partsFor(cab, cat);
    for (const p of parts) {
      if (p.materialId === 0) continue;
      // expand qty
      for (let q = 0; q < (p.qty ?? 1); q++) {
        const arr = byMaterial.get(p.materialId) ?? [];
        arr.push({
          partId: q === 0 ? p.id : `${p.id}_${q}`,
          partName: p.name,
          cabinetId: cab._id,
          cabinetClassId: cab.classId,
          w: p.w,
          h: p.h,
          grain: p.grain,
        });
        byMaterial.set(p.materialId, arr);
      }
    }
  }

  const unplaceable: ProjectNesting["unplaceable"] = [];
  const bins: NestBin[] = [];
  let totalArea = 0;
  let usedArea = 0;

  for (const [matId, parts] of byMaterial) {
    const material = cat.materials.get(matId);
    if (!material) {
      unplaceable.push({ partName: parts.map((p) => p.partName).join(", "), reason: `unknown material #${matId}` });
      continue;
    }
    const bin = nestMaterial(parts, material, unplaceable);
    bins.push(bin);
    totalArea += bin.totalArea;
    usedArea  += bin.usedArea;
  }

  // Stable sort by material name for predictable presentation.
  bins.sort((a, b) => a.materialName.localeCompare(b.materialName));

  return {
    bins,
    unplaceable,
    overallWastePercent: totalArea > 0 ? (totalArea - usedArea) / totalArea : 0,
  };
}
